/**
 * Forge CRUD operations for user notifications.
 * Path: users/{userId}/notifications/{notificationId}
 */
import { forge } from './ezhard';
import { where, orderBy, limit } from '@ezhard/sdk';
import type { QueryConstraint } from '@ezhard/sdk';

/* ─── Reference helpers ─── */

export function getNotificationsPath(userId: string): string {
  return `users/${userId}/notifications`;
}

/** Returns query constraints ordered by createdAt desc, limited to 50 documents. */
export function getNotificationsConstraints(): QueryConstraint[] {
  return [orderBy('createdAt', 'desc'), limit(50)];
}

/* ─── Mutations ─── */

/** Marks a single notification as read. */
export async function markNotificationRead(
  userId: string,
  notificationId: string,
): Promise<void> {
  await forge.doc(`users/${userId}/notifications/${notificationId}`).update({ read: true });
}

/** Marks all unread notifications as read using a batch write. */
export async function markAllRead(userId: string): Promise<void> {
  const unreadQuery = forge.collection(`users/${userId}/notifications`).query(
    where('read', '==', false),
  );

  const snapshot = await unreadQuery.get();
  if (snapshot.empty) return;

  const batch = forge.batch();
  snapshot.docs.forEach((docSnap) => {
    batch.update(docSnap.path, { read: true });
  });

  await batch.commit();
}

/** Deletes a single notification. */
export async function deleteNotification(
  userId: string,
  notificationId: string,
): Promise<void> {
  await forge.doc(`users/${userId}/notifications/${notificationId}`).delete();
}
